/**
 * RoomTypeSelector
 * Gruppierte Auswahl von Raumtypen (Innen/Außen)
 */

import { RoomType } from '@/types';
import { ROOMS, getIndoorRooms, getOutdoorRooms, RoomConfig } from '@/lib/roomConfig';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Zap } from 'lucide-react';

interface RoomTypeSelectorProps {
  value: RoomType;
  onChange: (type: RoomType) => void;
  customValue?: string;
  onCustomChange?: (value: string) => void;
}

export const RoomTypeSelector: React.FC<RoomTypeSelectorProps> = ({
  value,
  onChange,
  customValue,
  onCustomChange,
}) => {
  const indoorRooms = getIndoorRooms().filter(r => r.id !== 'custom');
  const outdoorRooms = getOutdoorRooms();
  const customRoom = ROOMS.custom;

  // Helper to render a single room button
  const renderRoomButton = (room: RoomConfig) => {
    const isSelected = value === room.id;
    return (
      <Button
        key={room.id}
        variant={isSelected ? "default" : "outline"}
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onChange(room.id as RoomType);
        }}
        className={`h-8 rounded-full px-3 text-xs ${isSelected ? '' : 'text-muted-foreground'}`}
        title={!room.supportsStaging ? 'Nur Leer (kein Staging)' : undefined}
      >
        {room.label}
        {!room.supportsStaging && (
          <span className="ml-1 opacity-70"><Zap className="w-3 h-3 fill-current" /></span>
        )}
      </Button>
    );
  };

  return (
    <div className="flex flex-col gap-3 mt-2">
      {/* Innenbereich */}
      <div>
        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5 block">
          Innenbereich
        </span>
        <div className="flex flex-wrap gap-2">
          {indoorRooms.map(renderRoomButton)}
        </div>
      </div>

      {/* Außenbereich */}
      <div>
        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5 block">
          Außenbereich
        </span>
        <div className="flex flex-wrap gap-2">
          {outdoorRooms.map(renderRoomButton)}
        </div>
      </div>

      {/* Sonstiges */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
        {renderRoomButton(customRoom)}
      </div>

      {/* Custom Label Input */}
      {value === RoomType.CUSTOM && onCustomChange && (
        <Input
          type="text"
          placeholder="Raumtyp eingeben (z.B. Heimkino)"
          className="h-8 text-sm"
          value={customValue || ''}
          onChange={(e) => onCustomChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Info: Staging nicht verfügbar */}
      {value && ROOMS[value] && !ROOMS[value].supportsStaging && (
        <div className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg flex items-center gap-2">
          <Zap className="w-3 h-3 fill-current" />
          Für diesen Raumtyp wird nur eine leere Version erstellt (kein virtuelles Staging).
        </div>
      )}
    </div>
  );
};
